import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { submitAssessment, smsAssessmentReport, emailAssessmentReport } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const fmtDollars = (cents) => `$${((cents || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const TRADES = [
  { v: "plumbing", l: "Plumbing", ticket: 620 },
  { v: "electrical", l: "Electrical", ticket: 550 },
  { v: "hvac", l: "HVAC", ticket: 880 },
  { v: "roofing", l: "Roofing", ticket: 4500 },
  { v: "general", l: "General / Remodeling", ticket: 5000 },
  { v: "landscaping", l: "Landscaping", ticket: 350 },
  { v: "painting", l: "Painting", ticket: 800 },
  { v: "sewer_drain", l: "Sewer and Drain", ticket: 850 },
  { v: "other", l: "Other", ticket: 500 },
];

const RESPONSE_OPTIONS = [
  { v: "under_5min", l: "Under 5 minutes" },
  { v: "under_1hr",  l: "5–60 minutes" },
  { v: "under_24hr", l: "A few hours" },
  { v: "over_24hr",  l: "Next day or later" },
];

const SCHEDULING_OPTIONS = [
  { v: "yes_automated", l: "Yes — automated reminders" },
  { v: "manual",        l: "I call / text manually" },
  { v: "none",          l: "No, nothing right now" },
];

export default function BusinessHealthAssessmentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const repSlug = params.get("rep") || null;
  const presenterMode = params.get("presenter") === "1";

  const [step, setStep] = useState(0); // 0=trade, 1..6=questions, 7=results
  const [trade, setTrade] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [inputs, setInputs] = useState({
    missed_calls_per_week: 5,
    avg_ticket_usd: 0,
    monthly_inquiries: 20,
    review_count: 0,
    google_rating: 4.3,
    photo_count: 8,
    response_tier: "under_24hr",
    scheduling_tool: "none",
    monthly_jobs: 0,
  });

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fontMult = presenterMode ? 1.5 : 1;

  // When user picks a trade, prefill the default ticket
  useEffect(() => {
    if (!trade) return;
    const t = TRADES.find((x) => x.v === trade);
    if (t) setInputs((prev) => ({ ...prev, avg_ticket_usd: t.ticket }));
  }, [trade]);

  const TOTAL_STEPS = 7;

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await submitAssessment({
        trade,
        business_name: businessName || null,
        owner_name: ownerName || null,
        phone: phone || null,
        email: email || null,
        rep_slug: repSlug,
        source: repSlug ? "thomas_field" : "self_serve",
        inputs: {
          ...inputs,
          monthly_jobs: Number(inputs.monthly_jobs) || Number(inputs.monthly_inquiries) || 0,
        },
      });
      setResult(r);
      setStep(TOTAL_STEPS);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't compute that — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const ctaToSignup = () => {
    const q = new URLSearchParams();
    if (repSlug) q.set("code", repSlug === "thomas" ? "TT1" : repSlug.toUpperCase());
    navigate(`/pricing?${q.toString()}`);
  };

  // ---------- RENDER ----------
  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "24px 16px 60px" }} data-testid="assessment-page">
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <Header repSlug={repSlug} presenterMode={presenterMode} step={step} totalSteps={TOTAL_STEPS} fontMult={fontMult} />

        {error && <div style={errBox}>{error}</div>}

        {step === 0 && (
          <Card title="WHAT'S YOUR TRADE?" subtitle="Pick the closest match — this sets the local benchmarks we'll use." fontMult={fontMult}>
            <div style={tileGrid}>
              {TRADES.map((t) => (
                <button
                  key={t.v}
                  onClick={() => { setTrade(t.v); next(); }}
                  data-testid={`trade-${t.v}`}
                  style={{ ...tile, ...(trade === t.v ? tileActive : {}), fontSize: 18 * fontMult }}>
                  {t.l}
                </button>
              ))}
            </div>
          </Card>
        )}

        {step === 1 && (
          <Card title="HOW MANY CALLS DO YOU MISS A WEEK?" subtitle="Be honest — this is the #1 source of lost revenue we'll measure." fontMult={fontMult}>
            <NumberStep value={inputs.missed_calls_per_week} setValue={(v) => setInputs({ ...inputs, missed_calls_per_week: v })} min={0} max={25} step={1} testid="q-missed-calls" suffix="missed calls/week" fontMult={fontMult} />
            <FooterNav onBack={back} onNext={next} backLabel="Trade" />
          </Card>
        )}

        {step === 2 && (
          <Card title="WHAT'S YOUR AVERAGE JOB WORTH?" subtitle="All-in — labor + materials, what the customer actually pays." fontMult={fontMult}>
            <DollarStep value={inputs.avg_ticket_usd} setValue={(v) => setInputs({ ...inputs, avg_ticket_usd: v })} testid="q-avg-ticket" fontMult={fontMult} />
            <FooterNav onBack={back} onNext={next} backLabel="Missed calls" />
          </Card>
        )}

        {step === 3 && (
          <Card title="HOW MANY NEW INQUIRIES PER MONTH?" subtitle="Calls + texts + web forms combined." fontMult={fontMult}>
            <NumberStep value={inputs.monthly_inquiries} setValue={(v) => setInputs({ ...inputs, monthly_inquiries: v, monthly_jobs: Math.max(Number(inputs.monthly_jobs) || 0, Math.floor(v * 0.6)) })} min={0} max={120} step={5} testid="q-monthly-inquiries" suffix="inquiries / month" fontMult={fontMult} />
            <FooterNav onBack={back} onNext={next} backLabel="Avg ticket" />
          </Card>
        )}

        {step === 4 && (
          <Card title="HOW MANY GOOGLE REVIEWS DO YOU HAVE?" subtitle="If you're not sure, type a guess — we'll show you the gap to your competitors." fontMult={fontMult}>
            <NumberStep value={inputs.review_count} setValue={(v) => setInputs({ ...inputs, review_count: v })} min={0} max={500} step={5} testid="q-review-count" suffix="Google reviews" fontMult={fontMult} />
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <SmallField label="STAR RATING">
                <select value={inputs.google_rating} onChange={(e) => setInputs({ ...inputs, google_rating: Number(e.target.value) })} style={selectStyle} data-testid="q-rating">
                  <option value={3.5}>3.5★ or below</option>
                  <option value={4.0}>4.0★</option>
                  <option value={4.3}>4.3★</option>
                  <option value={4.6}>4.6★</option>
                  <option value={4.9}>4.9★</option>
                </select>
              </SmallField>
              <SmallField label="PHOTOS">
                <select value={inputs.photo_count} onChange={(e) => setInputs({ ...inputs, photo_count: Number(e.target.value) })} style={selectStyle} data-testid="q-photos">
                  <option value={3}>Under 10</option>
                  <option value={12}>10 – 15</option>
                  <option value={20}>15+ photos</option>
                </select>
              </SmallField>
            </div>
            <FooterNav onBack={back} onNext={next} backLabel="Inquiries" />
          </Card>
        )}

        {step === 5 && (
          <Card title="HOW FAST DO YOU CALL NEW LEADS BACK?" subtitle="Speed-to-lead is the single biggest close-rate lever in home services." fontMult={fontMult}>
            <RadioList value={inputs.response_tier} setValue={(v) => setInputs({ ...inputs, response_tier: v })} options={RESPONSE_OPTIONS} testidPrefix="q-response" fontMult={fontMult} />
            <FooterNav onBack={back} onNext={next} backLabel="Reviews" />
          </Card>
        )}

        {step === 6 && (
          <Card title="HOW DO YOU CONFIRM APPOINTMENTS TODAY?" subtitle="Automated reminders cut no-shows by ~14 points on average." fontMult={fontMult}>
            <RadioList value={inputs.scheduling_tool} setValue={(v) => setInputs({ ...inputs, scheduling_tool: v })} options={SCHEDULING_OPTIONS} testidPrefix="q-scheduling" fontMult={fontMult} />

            <div style={{ marginTop: 20, padding: 14, background: BG, borderRadius: 10 }}>
              <SmallField label="OPTIONAL · BUSINESS NAME">
                <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} style={inputStyle} placeholder="Joe's Plumbing" data-testid="q-business" />
              </SmallField>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                <SmallField label="YOUR NAME">
                  <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} style={inputStyle} placeholder="Joe" data-testid="q-owner" />
                </SmallField>
                <SmallField label="PHONE (so we can text the report)">
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} placeholder="(401) 555-0100" data-testid="q-phone" />
                </SmallField>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button onClick={back} style={btnGhost} data-testid="step-back">← Back</button>
              <button onClick={submit} disabled={busy} style={{ ...btnPrimary, flex: 1, opacity: busy ? 0.6 : 1, fontSize: 18 * fontMult }} data-testid="run-assessment">
                {busy ? "Calculating…" : "SHOW MY LEAKAGE REPORT →"}
              </button>
            </div>
          </Card>
        )}

        {step === TOTAL_STEPS && result && (
          <ResultsScreen result={result} repSlug={repSlug} fontMult={fontMult} ctaToSignup={ctaToSignup} />
        )}

        <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 32, textAlign: "center" }}>
          Estimates based on your inputs and Rhode Island home-service industry benchmarks. Actual results vary by business.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// Results screen
// ============================================================
function ResultsScreen({ result, repSlug, fontMult, ctaToSignup }) {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [revealed, setRevealed] = useState(0);

  // Staggered reveal for dramatic effect
  useEffect(() => {
    const cards = result.category_breakdown.length;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setRevealed(i);
      if (i >= cards) clearInterval(t);
    }, 300);
    return () => clearInterval(t);
  }, [result]);

  return (
    <div data-testid="results-screen">
      <div style={{ background: "#fff", border: `2px solid ${GOLD}`, borderRadius: 18, padding: 22, marginBottom: 18 }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          {result.business_name ? `${result.business_name.toUpperCase()}'S` : "YOUR"} REVENUE LEAKAGE REPORT
        </div>
        <div style={{ color: "#5b5e69", fontSize: 13, marginBottom: 14 }}>
          {new Date(result.created_at).toLocaleDateString("en-US", { dateStyle: "long" })}
        </div>

        {result.category_breakdown.map((c, idx) => (
          <CategoryCard key={c.category}
                        card={c}
                        revealed={idx < revealed}
                        expanded={expandedIdx === idx}
                        onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                        fontMult={fontMult} />
        ))}

        <div style={{ height: 2, background: GOLD, margin: "20px 0" }} />
        <div style={{ background: NAVY, color: GOLD, padding: 22, borderRadius: 14, textAlign: "center" }} data-testid="leakage-total">
          <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>ESTIMATED ANNUAL REVENUE LEFT ON THE TABLE</div>
          <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 64 * fontMult, letterSpacing: 1, lineHeight: 1 }}>
            {fmtDollars(result.total_annual_leakage_cents)}
          </div>
          {result.capped_for_credibility && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#cbd5e1" }}>(Capped at $150k for credibility — your real number is likely higher.)</div>
          )}
        </div>

        <h3 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 22, marginTop: 22 }}>
          COMPARE TO A NEARNOW SUBSCRIPTION
        </h3>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }} data-testid="plan-compare-table">
          <thead style={{ background: BG }}>
            <tr>
              <th style={planTh}>NearNow Plan</th>
              <th style={planTh}>Annual Cost</th>
              <th style={planTh}>You Keep</th>
            </tr>
          </thead>
          <tbody>
            {result.plan_compare.map((p) => (
              <tr key={p.plan} style={{ borderTop: "1px solid #e5e7ee" }}>
                <td style={planTd}>{p.plan}</td>
                <td style={planTd}>{fmtDollars(p.annual_cents)}/yr</td>
                <td style={{ ...planTd, color: "#1A7A4A", fontWeight: 700 }}>{fmtDollars(p.kept_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <button onClick={ctaToSignup} style={{ ...ctaButton, fontSize: 18 * fontMult }} data-testid="cta-signup">
          CLAIM MY FOUNDING CONTRACTOR SPOT →
        </button>

        <TextMeReport assessmentId={result.id} prefillPhone={result.phone || ""} />
        <EmailMeReport assessmentId={result.id} prefillEmail={result.email || ""} />

        {repSlug && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF8E5", borderRadius: 10, fontSize: 13, color: "#5b5e69" }}>
            ✓ Your 10% rep discount code from <strong>{repSlug.toUpperCase()}</strong> will be applied automatically at checkout.
          </div>
        )}
      </div>
    </div>
  );
}

function TextMeReport({ assessmentId, prefillPhone }) {
  const [phone, setPhone] = useState(prefillPhone || "");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return;
    setState("sending");
    setError(null);
    try {
      const r = await smsAssessmentReport(assessmentId, phone.trim());
      if (r.ok) {
        setState("done");
      } else {
        setError("Couldn't send the text — your number was saved so we can call.");
        setState("error");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send right now.");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="text-me-success" style={{ marginTop: 14, padding: "14px 18px", background: "#E8F5EE", color: "#1A7A4A", borderRadius: 12, textAlign: "center", fontWeight: 700 }}>
        ✓ Text sent to {phone}. Check your phone for the report link.
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 14, background: "#FFF8E5", border: "1px dashed #D4AF37", borderRadius: 12, padding: 16 }} data-testid="text-me-form">
      <div style={{ color: "#0A1628", fontWeight: 700, letterSpacing: 1.5, fontSize: 14 }}>NOT READY TO SIGN UP YET?</div>
      <p style={{ color: "#5b5e69", fontSize: 14, margin: "6px 0 12px" }}>
        We&apos;ll text you a link to this full report so you can review it later with your partner / spouse / business advisor.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(401) 555-0100"
          data-testid="text-me-phone-input"
          style={{ flex: "1 1 220px", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", minHeight: 56 }}
        />
        <button
          type="submit"
          disabled={!phone.trim() || state === "sending"}
          data-testid="text-me-submit"
          style={{ background: "#0A1628", color: "#D4AF37", border: "none", padding: "14px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", minHeight: 56, opacity: state === "sending" ? 0.6 : 1 }}>
          {state === "sending" ? "Sending…" : "📱 TEXT ME THIS REPORT"}
        </button>
      </div>
      {error && <div data-testid="text-me-error" style={{ marginTop: 8, color: "#A32D2D", fontSize: 13 }}>{error}</div>}
    </form>
  );
}

function EmailMeReport({ assessmentId, prefillEmail }) {
  const [email, setEmail] = useState(prefillEmail || "");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setState("sending");
    setError(null);
    try {
      const r = await emailAssessmentReport(assessmentId, email.trim());
      if (r.ok) {
        setState("done");
      } else {
        setError("Couldn't send the email — your address was saved so we can follow up.");
        setState("error");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send right now.");
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="email-me-success" style={{ marginTop: 10, padding: "14px 18px", background: "#E8F5EE", color: "#1A7A4A", borderRadius: 12, textAlign: "center", fontWeight: 700 }}>
        ✓ Email sent to {email}. Check your inbox for the full report.
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 10, background: "#F5F7FA", border: "1px dashed #0A1628", borderRadius: 12, padding: 16 }} data-testid="email-me-form">
      <div style={{ color: "#0A1628", fontWeight: 700, letterSpacing: 1.5, fontSize: 14 }}>PREFER EMAIL INSTEAD?</div>
      <p style={{ color: "#5b5e69", fontSize: 14, margin: "6px 0 12px" }}>
        We&apos;ll email you a link to this full report so you can share it with your accountant or partner.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          data-testid="email-me-input"
          style={{ flex: "1 1 220px", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", minHeight: 56 }}
        />
        <button
          type="submit"
          disabled={!email.trim() || state === "sending"}
          data-testid="email-me-submit"
          style={{ background: "#0A1628", color: "#D4AF37", border: "none", padding: "14px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", minHeight: 56, opacity: state === "sending" ? 0.6 : 1 }}>
          {state === "sending" ? "Sending…" : "✉ EMAIL ME THIS REPORT"}
        </button>
      </div>
      {error && <div data-testid="email-me-error" style={{ marginTop: 8, color: "#A32D2D", fontSize: 13 }}>{error}</div>}
    </form>
  );
}

function CategoryCard({ card, revealed, expanded, onToggle, fontMult }) {  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12,
      padding: 16, marginBottom: 12,
      opacity: revealed ? 1 : 0,
      transform: revealed ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 400ms, transform 400ms",
    }} data-testid={`card-${card.category}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <div style={{ color: NAVY, fontWeight: 700, fontSize: 17 * fontMult }}>{card.label}</div>
        <div style={{ color: NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 34 * fontMult, letterSpacing: 1 }}>
          {fmtDollars(card.amount_cents)}<span style={{ fontSize: 14 * fontMult, color: "#5b5e69" }}>/yr</span>
        </div>
      </div>
      <div style={{ color: "#5b5e69", fontSize: 14 * fontMult, marginTop: 4 }}>{card.headline_copy}</div>
      <button onClick={onToggle} style={chevBtn} data-testid={`expand-${card.category}`}>
        {expanded ? "− Hide the math" : "+ How we calculated this"}
      </button>
      {expanded && (
        <div style={{ marginTop: 10, padding: 12, background: BG, borderRadius: 10, color: NAVY, fontSize: 13 * fontMult, lineHeight: 1.6 }} data-testid={`formula-${card.category}`}>
          {card.formula_plain}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Small components
// ============================================================
function Header({ repSlug, presenterMode, step, totalSteps, fontMult }) {
  const pct = step === 0 ? 0 : Math.round((step / totalSteps) * 100);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
            BUSINESS HEALTH SCORE & AI BUSINESS ASSESSMENT
          </div>
          <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1.5, fontSize: 32 * fontMult, color: NAVY, margin: "4px 0 0" }}>
            HOW MUCH REVENUE ARE YOU LEAKING?
          </h1>
          {repSlug && (
            <p style={{ color: "#5b5e69", fontSize: 13, margin: "4px 0 0" }}>
              Run by <strong>{repSlug.toUpperCase()}</strong> · 10% rep discount unlocks at checkout
            </p>
          )}
        </div>
        {presenterMode && <span style={{ background: GOLD, color: NAVY, padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 1.5 }}>PRESENTER MODE</span>}
      </div>
      {step > 0 && step < totalSteps && (
        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <div style={{ height: 8, background: "#e5e7ee", borderRadius: 4 }}>
            <div style={{ width: `${pct}%`, height: 8, background: GOLD, borderRadius: 4, transition: "width 250ms" }} />
          </div>
          <div style={{ color: "#5b5e69", fontSize: 12, marginTop: 4 }}>Question {step} of 6</div>
        </div>
      )}
    </>
  );
}

function Card({ title, subtitle, children, fontMult }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 18, padding: 22, marginTop: 14 }}>
      <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1.5, fontSize: 26 * fontMult, color: NAVY, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ color: "#5b5e69", fontSize: 14 * fontMult, marginTop: 4 }}>{subtitle}</p>}
      <div style={{ marginTop: 16 }}>{children}</div>
    </div>
  );
}

function NumberStep({ value, setValue, min, max, step, suffix, testid, fontMult }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }} data-testid={testid}>
      <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 64 * fontMult, color: NAVY, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ color: "#5b5e69", fontSize: 14 * fontMult }}>{suffix}</div>
      <input type="range" min={min} max={max} step={step}
             value={value}
             onChange={(e) => setValue(Number(e.target.value))}
             style={{ width: "100%", accentColor: NAVY }} />
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => setValue(Math.max(min, Number(value) - step))} style={pillBtn}>−</button>
        <button onClick={() => setValue(Math.min(max, Number(value) + step))} style={pillBtn}>+</button>
      </div>
    </div>
  );
}

function DollarStep({ value, setValue, testid, fontMult }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }} data-testid={testid}>
      <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 64 * fontMult, color: NAVY, lineHeight: 1 }}>
        ${Number(value || 0).toLocaleString("en-US")}
      </div>
      <div style={{ color: "#5b5e69", fontSize: 14 * fontMult }}>per job, all-in</div>
      <input type="number" value={value} onChange={(e) => setValue(Number(e.target.value))} style={{ ...inputStyle, fontSize: 22, textAlign: "center", maxWidth: 220 }} />
    </div>
  );
}

function RadioList({ value, setValue, options, testidPrefix, fontMult }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {options.map((o) => (
        <button key={o.v} onClick={() => setValue(o.v)}
                data-testid={`${testidPrefix}-${o.v}`}
                style={{ ...radioRow, ...(value === o.v ? radioRowActive : {}), fontSize: 17 * fontMult }}>
          <span style={{ marginRight: 12, fontSize: 22 }}>{value === o.v ? "●" : "○"}</span>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function SmallField({ label, children }) {
  return (
    <div>
      <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function FooterNav({ onBack, onNext, backLabel }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
      <button onClick={onBack} style={btnGhost} data-testid="step-back">← {backLabel}</button>
      <button onClick={onNext} style={{ ...btnPrimary, flex: 1 }} data-testid="step-next">CONTINUE →</button>
    </div>
  );
}

// ---------- styles ----------
const tileGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 };
const tile = { background: "#fff", border: "2px solid #e5e7ee", borderRadius: 12, padding: "20px 14px", fontWeight: 700, color: NAVY, cursor: "pointer", minHeight: 64 };
const tileActive = { borderColor: NAVY, background: NAVY, color: GOLD };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box" };
const selectStyle = { ...inputStyle, background: "#fff" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 1.5, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", minHeight: 56 };
const btnGhost = { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, padding: "14px 18px", borderRadius: 12, fontWeight: 700, letterSpacing: 1.5, fontSize: 14, cursor: "pointer", minHeight: 56 };
const ctaButton = { display: "block", width: "100%", marginTop: 18, background: GOLD, color: NAVY, border: "none", padding: "22px 24px", borderRadius: 14, fontWeight: 700, letterSpacing: 2, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", textDecoration: "none", textAlign: "center" };
const pillBtn = { background: NAVY, color: GOLD, border: "none", width: 56, height: 56, borderRadius: 28, fontSize: 28, fontWeight: 700, cursor: "pointer" };
const radioRow = { background: "#fff", border: "2px solid #e5e7ee", borderRadius: 12, padding: "18px 16px", textAlign: "left", color: NAVY, fontWeight: 600, cursor: "pointer", minHeight: 64, display: "flex", alignItems: "center" };
const radioRowActive = { borderColor: NAVY, background: "#F4F1E6" };
const chevBtn = { marginTop: 8, background: "transparent", border: "none", color: NAVY, fontWeight: 700, letterSpacing: 1, fontSize: 13, cursor: "pointer", padding: 0 };
const planTh = { textAlign: "left", padding: "10px 12px", color: NAVY, fontSize: 11, letterSpacing: 1.5, fontWeight: 700 };
const planTd = { padding: "10px 12px", color: NAVY, fontSize: 14 };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginTop: 14, fontSize: 14 };
